import {Tab, TabProps, Tabs, TabsProps} from '@mui/material';
import {useTenantSettingHelper} from 'hooks/settings';
import {useEffect, useMemo, useState} from 'react';

interface AppTabProp extends Omit<TabProps, 'key' | 'label'> {}
export interface TabsType<T extends string | number> {
    title: string;
    key: T;
    component?: React.ReactNode;
    permissions?: string[];
}
interface AppTabsProps<T extends string | number> extends Omit<TabsProps, 'value' | 'onChange'> {
    tabs: TabsType<T>[];
    tabProps?: AppTabProp;
    selectedTabValue: T;
    handleAfterChange?: (value: T) => void;
    scrollableEvent?: boolean;
    loading?: boolean;
}

export function AppTabs<T extends string | number>({
    selectedTabValue,
    handleAfterChange,
    scrollableEvent,
    tabs,
    tabProps,
    loading,
    ...rest
}: AppTabsProps<T>) {
    const handleChange = (value: T) => {
        if (handleAfterChange) {
            handleAfterChange(value);
        }
        setSelectedValue(value);
    };
    const {isSettingEnabled} = useTenantSettingHelper();
    const [selectedValue, setSelectedValue] = useState<T>(selectedTabValue);
    useEffect(() => {
        setSelectedValue(selectedTabValue);
    }, [selectedTabValue]);

    const renderTab = useMemo(() => {
        return tabs.filter(t => isSettingEnabled(t.permissions ?? [])) ?? [];
    }, [tabs]);
    const selecteTab = useMemo(
        () => renderTab.find(tab => tab.key === selectedValue)?.component,
        [selectedValue, renderTab],
    );

    return (
        <>
            <Tabs {...rest} value={selectedValue} variant="scrollable">
                {renderTab.map(tab => (
                    <Tab
                        {...tabProps}
                        key={`${tab.key}`}
                        label={tab.title}
                        onClick={() => {
                            handleChange(tab.key);
                        }}
                        value={tab.key}
                    />
                ))}
            </Tabs>
            {selecteTab && <>{selecteTab}</>}
        </>
    );
}

export default AppTabs;
